/**
 * The composer's paperclip — including the state where it says no (#758).
 *
 * ── Off BEFORE they try, not after ─────────────────────────────────────
 *
 * Until now the button worked for everyone: the picker opened, the member
 * chose a file, and only then a toast said `view-only: link existing
 * files` — a developer's shorthand, on a notice that disappears, after
 * they had already done the work. Disabled here it answers at the only
 * moment the answer is cheap.
 *
 * ── The hover lives on the WRAPPER ─────────────────────────────────────
 *
 * A disabled button fires no mouse events in several browsers, so a
 * tooltip attached to the button itself would simply never appear.
 *
 * ── Its own component because ChatPanel has no translations ────────────
 *
 * Every string in that file is hardcoded English. Three new sentences — the
 * ones a member actually has to understand — should not be, and adding a
 * translation hook to a 2000-line file that uses none everywhere else
 * would leave the next reader unsure which convention applies.
 */
import { useTranslation } from '@papan/i18n'
import { Paperclip } from 'lucide-react'
import { Tooltip } from '@papan/core-ui/ui'

export function ComposerAttachButton({ canWrite, storageFull, onPick }: {
  canWrite: boolean
  /**
   * Her OWN chat storage is full, from the figure `ChatPanel` already
   * fetches for the banner above the composer (todo 10) — so this answers
   * on the FIRST attempt rather than after a refusal.
   *
   * The organisation's quota is deliberately NOT here: the chat has no
   * source for it, and remembering a refusal to cover it needs clearing
   * wired at every site that frees space — missed twice in review, and a
   * stale one leaves a dead button, which is worse than the refusal.
   */
  storageFull?: 'member' | null
  onPick: () => void
}) {
  const { t } = useTranslation('agent')

  // ⚠️ AFTER `canWrite`, not before. Read-only is the broader refusal: in a
  // view she cannot write at all, "your storage is full" is both wrong and
  // a disclosure about her account that the view should not be making.
  if (canWrite && storageFull) {
    return (
      <Tooltip content={t('attachStorageFullMine')} placement="top">
        <span className="inline-flex shrink-0">
          <button
            type="button"
            disabled
            aria-label={t('attachStorageFullMine')}
            className="themed-text-muted opacity-40 cursor-not-allowed shrink-0 p-0.5"
          >
            <Paperclip size={14} />
          </button>
        </span>
      </Tooltip>
    )
  }

  if (canWrite) {
    return (
      <button
        type="button"
        onClick={onPick}
        className="themed-text-muted hover:themed-text transition-colors shrink-0 p-0.5"
        title={t('attachFile')}
      >
        <Paperclip size={14} />
      </button>
    )
  }

  return (
    <Tooltip content={t('attachViewOnly')} placement="top">
      <span className="inline-flex shrink-0">
        <button
          type="button"
          disabled
          aria-label={t('attachNeedsWrite')}
          className="themed-text-muted opacity-40 cursor-not-allowed shrink-0 p-0.5"
        >
          <Paperclip size={14} />
        </button>
      </span>
    </Tooltip>
  )
}
